
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Typography,
    Chip,
    Box,
    IconButton,
    Tooltip,
    Divider,
    Paper,
    List,
    ListItem,
    ListItemText
} from '@mui/material';
import {
    ContentCopy,
    Close,
    CreditScore,
    Person,
    Home,
    Email,
    LocationOn,
    LocalPhone
} from '@mui/icons-material';

const ENFIN_FORM_URL = "https://ho.enfin.com/prequalification/nv24IdJoPv";

const PrequalAssistant = ({ open, onClose, candidate }) => {
    const [copiedField, setCopiedField] = useState(null);

    if (!candidate) return null;

    // Extract key data for copy-paste
    // Handle both flat structure (if passed prepaid) or deep structure (raw API response)
    const ownerNames = candidate.ownerNames || candidate.ownership?.currentOwners?.ownerNames || [];
    const primaryOwner = ownerNames[0] || {};

    // Some APIs return email directly on ownership or inside the owner object
    const email = primaryOwner.email || primaryOwner.emails?.[0] || candidate.ownership?.currentOwnerMailingInfo?.email || '';
    const phone = primaryOwner.phoneNumber || primaryOwner.phones?.[0]?.number || '';

    const address = candidate.propertyAddress ?
        `${candidate.propertyAddress.streetAddress}, ${candidate.propertyAddress.city}, ${candidate.propertyAddress.state} ${candidate.propertyAddress.zipCode}`
        : '';

    const copyToClipboard = (text, label) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopiedField(label);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const CopyChip = ({ icon, label, value }) => (
        <Tooltip title={copiedField === label ? "Copied!" : `Copy ${label}`} arrow>
            <Chip
                icon={icon}
                label={
                    <Box display="flex" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 'bold', mr: 1, opacity: 0.7, color: copiedField === label ? '#fff' : '#000' }}>
                            {label}:
                        </Typography>
                        <Typography variant="caption" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', color: copiedField === label ? '#fff' : '#333', fontWeight: 500 }}>
                            {value || 'N/A'}
                        </Typography>
                    </Box>
                }
                onClick={() => copyToClipboard(value, label)}
                onDelete={() => copyToClipboard(value, label)}
                deleteIcon={<ContentCopy sx={{ fontSize: '14px !important', color: copiedField === label ? '#fff !important' : '#666 !important' }} />}
                sx={{
                    m: 0.5,
                    justifyContent: 'flex-start',
                    height: 32,
                    backgroundColor: copiedField === label ? '#4caf50' : '#e0e0e0',
                    color: copiedField === label ? '#fff' : '#000',
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: copiedField === label ? '#43a047' : '#d5d5d5' }
                }}
            />
        </Tooltip>
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: { height: '85vh', maxHeight: '85vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: '1px solid #eee' }}>
                <Box display="flex" alignItems="center">
                    <CreditScore color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Prequalification Assistant</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <Grid container sx={{ height: '100%' }}>

                    {/* RESPONSIVE LAYOUT: Stack on mobile, Side-by-side on desktop */}

                    {/* LEFT PANEL: Assistant / Data Source */}
                    <Grid item xs={12} md={3} sx={{
                        borderRight: { md: '1px solid #ddd' },
                        borderBottom: { xs: '1px solid #ddd', md: 'none' },
                        bgcolor: '#f9f9f9',
                        display: 'flex',
                        flexDirection: 'column',
                        height: { xs: 'auto', md: '100%' },
                        maxHeight: { xs: '30%', md: '100%' },
                        overflowY: 'auto'
                    }}>
                        <Box p={2}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                QUICK COPY HELPER
                            </Typography>

                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                                <CopyChip
                                    icon={<Person />}
                                    label="First Name"
                                    value={primaryOwner.fullName ? primaryOwner.fullName.split(' ')[0] : ''}
                                />
                                <CopyChip
                                    icon={<Person />}
                                    label="Last Name"
                                    value={primaryOwner.fullName ? primaryOwner.fullName.split(' ').slice(1).join(' ') : ''}
                                />
                                <CopyChip
                                    icon={<Home />}
                                    label="Street"
                                    value={candidate.propertyAddress?.streetAddress}
                                />
                                <CopyChip
                                    icon={<LocationOn />}
                                    label="City"
                                    value={candidate.propertyAddress?.city}
                                />
                                <CopyChip
                                    icon={<LocationOn />}
                                    label="Zip"
                                    value={candidate.propertyAddress?.zipCode}
                                />
                                {email && (
                                    <CopyChip
                                        icon={<Email />}
                                        label="Email"
                                        value={email}
                                    />
                                )}
                                {phone && (
                                    <CopyChip
                                        icon={<LocalPhone />}
                                        label="Phone"
                                        value={phone}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    {/* RIGHT PANEL: The Form Iframe */}
                    <Grid item xs={12} md={9} sx={{ height: { xs: '70%', md: '100%' }, bgcolor: '#fff' }}>
                        <iframe
                            src={ENFIN_FORM_URL}
                            title="Enfin Prequalification"
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default PrequalAssistant;
